package pl.decks.personal.pages.ievp.sections.experience

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.colorMix
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.selectors.lastChild
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import pl.decks.personal.components.animations.AppearAnimation
import pl.decks.personal.components.widgets.responsivegrid.ResponsiveGridStylesheet
import pl.decks.personal.components.widgets.timeline.MonthlyTimelineStylesheet
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout


object ExperienceSectionStylesheet {
    object Vars {
        val timelineObjHeight by StyleVariable(6.5.cssRem)

        val timelineOptionAccentColor by StyleVariable<Color>()
        val timelineBackgroundColor by StyleVariable<Color>(Colors.Transparent)
        val timelineOptionBackgroundColor by StyleVariable<Color>()

        val underwriteColor by StyleVariable<Color>()
        val experienceBreakdownColor by StyleVariable<Color>()

        val interactionEffectDuration by StyleVariable(TransitionDurationVars.Normal.value())
        val entrySwitchEffectDuration by StyleVariable(TransitionDurationVars.Slow.value())
    }

    object CircleSelector {
        val Options = CssStyle.base {
            Modifier
                .flexWrap(FlexWrap.Wrap)
                .gap(SiteLayout.Space.XSmall)
                .rowGap(SiteLayout.Space.XSmall)
        }

        sealed interface Kind : ComponentKind

        val Style = CssStyle<Kind> {
            val bgColor = Vars.timelineOptionBackgroundColor.value(Vars.timelineBackgroundColor.value())
            base {
                Modifier
                    .aspectRatio(1)
                    .height(Vars.timelineObjHeight.value())
                    .flexShrink(0)
                    .borderRadius(SiteLayout.Rounding.Full)
                    .padding(SiteLayout.Space.Medium)
                    .border(
                        width = SiteLayout.Line.Normal,
                        style = LineStyle.Solid,
                        color = Colors.Transparent,
                    )
                    .backgroundColor(bgColor)
                    .overflow(Overflow.Clip)
                    .transition {
                        property("background-color", "border-color")
                        timingFunction(TransitionTimingFunction.EaseInOut)
                        duration(Vars.interactionEffectDuration.value())
                    }
            }
            hover {
                Modifier
                    .border(
                        width = SiteLayout.Line.Normal,
                        style = LineStyle.Solid,
                        color = Vars.timelineOptionAccentColor.value(colorMode.toPalette().color),
                    )
                    .backgroundColor(
                        colorMix(
                            interpolation = ColorInterpolationMethod.Hsl,
                            color1 = Vars.timelineOptionAccentColor.value(colorMode.toPalette().color) to 20.percent,
                            color2 = bgColor to 80.percent,
                        )
                    )
            }
            active {
                Modifier
                    .border(
                        width = SiteLayout.Line.Normal,
                        style = LineStyle.Solid,
                        color = Vars.timelineOptionAccentColor.value(colorMode.toPalette().color),
                    )
                    .backgroundColor(
                        colorMix(
                            interpolation = ColorInterpolationMethod.Hsl,
                            color1 = Vars.timelineOptionAccentColor.value(colorMode.toPalette().color) to 30.percent,
                            color2 = bgColor to 70.percent,
                        )
                    )
            }
        }
        val SelectedVariant = Style.addVariant {
            base {
                Modifier
                    .border(
                        width = SiteLayout.Line.Normal,
                        style = LineStyle.Solid,
                        color = Vars.timelineOptionAccentColor.value(colorMode.toPalette().color),
                    )
                    .backgroundColor(
                        colorMix(
                            interpolation = ColorInterpolationMethod.Hsl,
                            color1 = Vars.timelineOptionAccentColor.value(colorMode.toPalette().color) to 30.percent,
                            color2 = Vars.timelineOptionBackgroundColor.value(Vars.timelineBackgroundColor.value()) to 70.percent,
                        )
                    )
            }
        }
    }

    object Breakdown {
        val Container = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .setVariable(ResponsiveGridStylesheet.Vars.gridGap, SiteLayout.Space.XXSmall)
                .setVariable(ResponsiveGridStylesheet.Vars.borderRadius, SiteLayout.Rounding.Medium)
                .setVariable(ResponsiveGridStylesheet.Vars.borderWidth, SiteLayout.Line.Hairline)
                .setVariable(
                    ResponsiveGridStylesheet.Vars.focusTransitionDuration,
                    Vars.entrySwitchEffectDuration.value()
                )
                .setVariable(ResponsiveGridStylesheet.Vars.focusColor, Vars.experienceBreakdownColor.value())
                .setVariable(ResponsiveGridStylesheet.Vars.focusBorderColor, Vars.experienceBreakdownColor.value())
        }
        val CompanyHeader = CssStyle.base {
            Modifier
                .gap(SiteLayout.Space.Medium)
                .padding(SiteLayout.Space.Medium)
                .transition {
                    property("opacity")
                    timingFunction(TransitionTimingFunction.EaseInOut)
                    duration(Vars.entrySwitchEffectDuration.value())
                }
        }
        val CompanyLogoContainer = CssStyle.base {
            Modifier
                .height(Vars.timelineObjHeight.value() * 1.2)
                .aspectRatio(1)
                .borderRadius(SiteLayout.Rounding.Large)
                .padding(SiteLayout.Space.Medium)
                .overflow(Overflow.Clip)
                .backgroundColor(Vars.timelineOptionBackgroundColor.value(Colors.Transparent))
                .border(
                    width = SiteLayout.Line.Hairline,
                    style = LineStyle.Solid,
                    color = Vars.experienceBreakdownColor.value(),
                )
        }
        val Divider = CssStyle.base {
            Modifier
                .borderTop(
                    SiteLayout.Line.Hairline,
                    LineStyle.Solid,
                    Vars.underwriteColor.value(Vars.experienceBreakdownColor.value()),
                )
                .margin(SiteLayout.Space.none)
        }
        val CompanyName = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fillMaxWidth()
                .padding(topBottom = SiteLayout.Space.Small)
                .fontWeight(FontTypography.Weight.Bold)
                .fontSize(FontTypography.Size.XLarge)
        }
        val Underwrite = FontTypography.mono.extendedByBase {
            Modifier
                .color(Vars.underwriteColor.value(Vars.experienceBreakdownColor.value()))
                .fontSize(FontTypography.Size.Small)
        }
        val InformationContainer = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .backgroundColor(Vars.timelineBackgroundColor.value())
                .overflow(Overflow.Clip)
                .borderRadius(
                    bottomLeft = SiteLayout.Space.Medium,
                    bottomRight = SiteLayout.Space.Medium,
                )
                .border(
                    width = SiteLayout.Line.Hairline,
                    style = LineStyle.Solid,
                    color = Vars.experienceBreakdownColor.value(),
                )
                .transition {
                    property("border-color")
                    timingFunction(TransitionTimingFunction.EaseInOut)
                    duration(Vars.entrySwitchEffectDuration.value())
                }
        }
        val InformationContainerFlavorBG = CssStyle.base {
            val flavourColor = colorMix(
                interpolation = ColorInterpolationMethod.Hsl,
                color1 = Vars.experienceBreakdownColor.value() to 10.percent,
                color2 = Colors.Transparent to 90.percent,
            )
            Modifier.fillMaxSize().backgroundImage(
                linearGradient(LinearGradient.Direction.ToBottomRight, ColorInterpolationMethod.Hsl) {
                    add(Colors.Transparent, 0.percent)
                    add(flavourColor, 20.percent)
                    add(Colors.Transparent, 25.percent)
                    add(flavourColor, 52.percent)
                    add(Colors.Transparent, 75.percent)
                    add(flavourColor, 80.percent)
                    add(Colors.Transparent, 100.percent)
                }
            )
                .animation(
                    AppearAnimation.Frames.toAnimation(
                        duration = Vars.entrySwitchEffectDuration.value() * 2,
                        timingFunction = AnimationTimingFunction.EaseInOut,
                        fillMode = AnimationFillMode.Both,
                    )
                )
        }
        val BottomNavigation = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Stretch)
        }


        sealed interface NavButtonKind : ComponentKind

        val NavigationButton = CssStyle<NavButtonKind> {
            base {
                Modifier
                    .fillMaxWidth()
                    .borderTop(
                        width = SiteLayout.Line.Hairline,
                        style = LineStyle.Solid,
                        color = Vars.experienceBreakdownColor.value()
                    )
                    .borderRight(
                        width = SiteLayout.Line.Hairline,
                        style = LineStyle.Solid,
                        color = Vars.experienceBreakdownColor.value()
                    )
                    .color(Vars.experienceBreakdownColor.value())
                    .transition {
                        property("color", "background-color")
                        timingFunction(TransitionTimingFunction.EaseInOut)
                        duration(
                            Vars.entrySwitchEffectDuration.value(),
                            Vars.interactionEffectDuration.value(),
                        )
                    }
            }

            lastChild {
                Modifier.borderRight(SiteLayout.Line.none)
            }
        }
        val ActiveNavigationButtonVariant = NavigationButton.addVariant {
            hover {
                Modifier.backgroundColor(
                    colorMix(
                        interpolation = ColorInterpolationMethod.Hsl,
                        color1 = Vars.experienceBreakdownColor.value() to 20.percent,
                        color2 = Colors.Transparent to 80.percent,
                    )
                )
            }
            active {
                Modifier.backgroundColor(
                    colorMix(
                        interpolation = ColorInterpolationMethod.Hsl,
                        color1 = Vars.experienceBreakdownColor.value() to 30.percent,
                        color2 = Colors.Transparent to 70.percent,
                    )
                )
            }
        }

    }

    object BreakdownPanel {
        val Content = CssStyle.base {
            Modifier.fillMaxWidth().padding(SiteLayout.Space.Medium)
        }

        val PrimaryText = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fontWeight(FontTypography.Weight.Bold)
                .fontSize(FontTypography.Size.XXLarge)
                .margin(bottom = SiteLayout.Space.Small)
                .color(Vars.experienceBreakdownColor.value())
                .textAlign(TextAlign.Center)
        }

        val Foreword = CssStyle {
            base {
                Modifier
                    .fontStyle(FontStyle.Italic)
                    .maxWidth(16.cssRem)
                    .textAlign(TextAlign.Center)
            }
            Breakpoint.MD {
                Modifier.maxWidth(22.cssRem)
            }
        }

        val TextEmphasis = CssStyle.base {
            Modifier
                .color(Vars.experienceBreakdownColor.value())
                .fontWeight(FontTypography.Weight.Medium)
        }
        val SecondaryText = FontTypography.serif.extendedByBase {
            Modifier
                .fillMaxWidth()
                .textAlign(TextAlign.End)
                .fontSize(FontTypography.Size.Normal)
                .padding(topBottom = SiteLayout.Space.XSmall)
        }
        val Ornate = CssStyle.base {
            Modifier
                .fontSize(2.em)
                .lineHeight(1)
        }
    }

    val Timeline = CssStyle.base {
        Modifier
            .backgroundColor(Vars.timelineBackgroundColor.value())
            .borderRadius(SiteLayout.Rounding.Full)
            .padding(leftRight = SiteLayout.Space.Small)
            .height(Vars.timelineObjHeight.value())
            .minWidth(100.percent)
            .width(90.vw)
            .overflow(Overflow.Clip)
    }

    val Section = CssStyle.base {
        val sitePalette = colorMode.toSitePalette()
        Modifier
            .minHeight(100.vh)
            .fillMaxWidth()
            .setVariable(Vars.timelineBackgroundColor, sitePalette.surface)
            .setVariable(MonthlyTimelineStylesheet.Vars.yearLabelColor, sitePalette.accents.primary)
            .setVariable(
                MonthlyTimelineStylesheet.Vars.yearBreakColor,
                sitePalette.accents.primary.toRgb().copyf(alpha = 0.5f)
            )
            .gap(SiteLayout.Space.XLarge)
            .padding(topBottom = SiteLayout.Space.XXLarge)
    }
}
